import React, { useEffect, useRef } from "react";
import "../iot/iot-shell.css";

const MATERIAL_SYMBOLS_URL =
  "https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@24,400,1,0";
const ROBOTO_URL =
  "https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap";

function loadStylesheet(id, href) {
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

function loadMaterialWeb() {
  if (window.__iotMaterialWebReady) {
    return Promise.resolve();
  }

  if (!document.getElementById("iot-material-importmap")) {
    const importMap = document.createElement("script");
    importMap.id = "iot-material-importmap";
    importMap.type = "importmap";
    importMap.textContent = JSON.stringify({
      imports: { "@material/web/": "https://esm.run/@material/web/" },
    });
    document.head.appendChild(importMap);
  }

  return new Promise((resolve, reject) => {
    const onReady = () => {
      window.removeEventListener("iot-material-ready", onReady);
      resolve();
    };
    window.addEventListener("iot-material-ready", onReady);

    if (!document.getElementById("iot-material-web-loader")) {
      const loader = document.createElement("script");
      loader.id = "iot-material-web-loader";
      loader.type = "module";
      loader.textContent = `
        import '@material/web/all.js';
        import { styles } from '@material/web/typography/md-typescale-styles.js';
        if (!document.adoptedStyleSheets.includes(styles.styleSheet)) {
          document.adoptedStyleSheets.push(styles.styleSheet);
        }
        window.__iotMaterialWebReady = true;
        window.dispatchEvent(new Event('iot-material-ready'));
      `;
      loader.onerror = () => reject(new Error("Failed to load Material Web"));
      document.head.appendChild(loader);
    } else if (window.__iotMaterialWebReady) {
      window.removeEventListener("iot-material-ready", onReady);
      resolve();
    }
  });
}

/**
 * Full IoT facility control panel — mounts the original AppCore inside DisasterMS layout.
 * Backend URL: set REACT_APP_IOT_BACKEND_URL in .env
 */
export default function IoTSensors() {
  const controllerRef = useRef(null);

  useEffect(() => {
    loadStylesheet("iot-material-symbols", MATERIAL_SYMBOLS_URL);
    loadStylesheet("iot-roboto", ROBOTO_URL);

    let cancelled = false;

    const boot = async () => {
      try {
        await loadMaterialWeb();
      } catch (err) {
        console.error("Material Web Components failed to load:", err);
        return;
      }

      if (cancelled) return;

      const { default: AppCore } = await import("../iot/core.js");
      controllerRef.current = new AppCore();
      window.appController = controllerRef.current;
    };

    boot();

    return () => {
      cancelled = true;
      if (controllerRef.current?.destroy) {
        controllerRef.current.destroy();
      }
      controllerRef.current = null;
      window.appController = null;
    };
  }, []);

  useEffect(() => {
    const sidebar = document.getElementById("iot-sidebar");
    const overlay = document.getElementById("iot-sidebar-overlay");
    const menuBtn = document.getElementById("iot-mobile-menu-btn");
    const closeBtn = document.getElementById("iot-close-sidebar-btn");

    if (!sidebar || !overlay) return;

    const toggleSidebar = () => {
      const isClosed = sidebar.classList.contains("-translate-x-full");
      if (isClosed) {
        sidebar.classList.remove("-translate-x-full");
        overlay.classList.remove("hidden");
        setTimeout(() => overlay.classList.remove("opacity-0"), 10);
      } else {
        sidebar.classList.add("-translate-x-full");
        overlay.classList.add("opacity-0");
        setTimeout(() => overlay.classList.add("hidden"), 300);
      }
    };

    const onHashChange = () => {
      const isOpen = !sidebar.classList.contains("-translate-x-full");
      if (isOpen) toggleSidebar();
    };
    
    menuBtn?.addEventListener("click", toggleSidebar);
    closeBtn?.addEventListener("click", toggleSidebar);
    overlay?.addEventListener("click", toggleSidebar);
    window.addEventListener("hashchange", onHashChange);

    const modalOverlay = document.getElementById("modal-overlay");
    const modalCloseBtn = modalOverlay?.querySelector("md-filled-button");
    const closeModal = () => modalOverlay?.classList.add("hidden");
    modalCloseBtn?.addEventListener("click", closeModal);

    return () => {
      menuBtn?.removeEventListener("click", toggleSidebar);
      closeBtn?.removeEventListener("click", toggleSidebar);
      overlay?.removeEventListener("click", toggleSidebar);
      window.removeEventListener("hashchange", onHashChange);
      modalCloseBtn?.removeEventListener("click", closeModal);
    };
  }, []);

  return (
    <div className="iot-shell flex-1 min-h-0 flex overflow-hidden text-gray-800 font-['Roboto',sans-serif]">
      {/* Auth overlay */}
      <div
        id="auth-overlay"
        className="fixed inset-0 z-[100] bg-gray-900/60 backdrop-blur-md flex items-center justify-center animate-fade-in"
      >
        <div
          id="auth-box"
          className="bg-white rounded-[1.5rem] p-8 shadow-2xl w-full max-w-sm flex flex-col items-center text-center gap-6"
        >
          <div
            id="auth-icon-bg"
            className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center text-iot-primary mb-2"
          >
            <span id="auth-icon" className="material-symbols-rounded text-4xl">
              lock
            </span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Security Check</h2>
            <p className="text-gray-500 mt-1">Please enter your access key</p>
          </div>
          <div className="w-full flex flex-col gap-2">
            <md-outlined-text-field
              id="auth-input"
              label="Access Key"
              type="password"
              className="w-full"
            />
            <p id="auth-feedback" className="text-sm font-medium h-5 text-transparent">
              .
            </p>
          </div>
          <md-filled-button id="auth-btn" className="w-full">
            Unlock Dashboard
          </md-filled-button>
          <p id="auth-attempts" className="text-xs text-gray-400">
            Attempts remaining: 5
          </p>
        </div>
      </div>

      {/* Modal overlay */}
      <div
        id="modal-overlay"
        className="fixed inset-0 z-[110] bg-gray-900/20 backdrop-blur-sm hidden flex items-center justify-center animate-fade-in"
      >
        <div className="bg-white rounded-[1.5rem] p-6 shadow-2xl w-[90vw] max-w-sm flex flex-col items-center text-center gap-4 animate-scale-in">
          <div
            id="modal-icon-bg"
            className="w-14 h-14 rounded-full flex items-center justify-center"
          >
            <span id="modal-icon" className="material-symbols-rounded text-3xl">
              info
            </span>
          </div>
          <div>
            <h3 id="modal-title" className="text-xl font-medium text-gray-900">
              Notification
            </h3>
            <p
              id="modal-message"
              className="text-sm text-gray-500 mt-2 leading-relaxed"
            />
          </div>
          <div className="w-full pt-2">
            <md-filled-button className="w-full">Okay</md-filled-button>
          </div>
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      <div
        id="iot-sidebar-overlay"
        className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-sm hidden md:hidden opacity-0 transition-opacity duration-300"
      />

      {/* IoT inner sidebar */}
      <aside
        id="iot-sidebar"
        className="fixed md:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-100 flex flex-col min-h-0 h-full flex-shrink-0 transform -translate-x-full md:translate-x-0 transition-transform duration-300 ease-in-out"
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200">
              <span className="material-symbols-rounded">memory</span>
            </div>
            <div>
              <h1 className="font-bold text-gray-900 leading-tight">IoT Access</h1>
              <p className="text-xs text-gray-500 uppercase tracking-wider font-bold">
                Control Panel
              </p>
            </div>
          </div>
          <button
            id="iot-close-sidebar-btn"
            type="button"
            className="md:hidden text-gray-400 hover:bg-gray-100 hover:text-gray-700 p-1 rounded-full transition-colors"
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-1" id="nav-links" />

        {/* <div className="p-4 border-t border-gray-100">
          <button
            id="btn-logout"
            type="button"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <span className="material-symbols-rounded">logout</span>
            Secure Logout
          </button>
        </div> */}
      </aside>

      {/* Main content */}
      <main className="flex-1 min-h-0 flex flex-col overflow-hidden relative w-full min-w-0 bg-[#f8f9fa]">
        <header className="md:hidden flex-shrink-0 flex items-center justify-between bg-white p-4 border-b border-gray-200 z-30">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white shadow-md">
              <span className="material-symbols-rounded text-sm">memory</span>
            </div>
            <h2 className="font-bold text-gray-900 leading-tight">IoT Access</h2>
          </div>
          <button
            id="iot-mobile-menu-btn"
            type="button"
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
          >
            <span className="material-symbols-rounded">menu</span>
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6 lg:p-10" id="app-container" />
        </div>
      </main>
    </div>
  );
}
